import { replaceRoute } from '../../lib/router.js';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import StakeholdersSettingsFrom from '../../components/projects/Stakeholders/StakeholdersSettingsForm.jsx';
import SubHeader from '../../components/SubHeader.jsx';
import {
  retrieveProject,
  selectProject,
  updateProject,
} from '../../modules/projects.js';
import {
  getEditorSaveDataPromise,
  getReadyStatusPromise,
} from '../../components/editor/defaultOptions.js';
import { mapFinalFormErrors } from '../../lib/utils.js';
import { stakeholdersSettingsValidate } from './utils/index.js';
import StakeholdersSettingsHeader from '../../components/headers/PageHeaders/Project/Stakeholders/StakeholderSettingsHeader.jsx';

const mapErrors = mapFinalFormErrors('Failed to save');

const mapStateToProps = (state, ownProps) => ({
  projectId: ownProps.projectId,
  project: selectProject(ownProps.projectId)(state),
});

const mapDispatchToProps = {
  retrieveProject,
  updateProject,
};

function StakeholderSettingsFormContainer(props) {
  const { projectId, project, retrieveProject, updateProject } = props;

  const [columns, setColumns] = useState(null);
  const [initial, setInitial] = useState({
    topLeftQuadrantLabel: '',
    topRightQuadrantLabel: '',
    bottomLeftQuadrantLabel: '',
    bottomRightQuadrantLabel: '',
  });
  const [commentEditorInstance, setCommentEditorInstance] = useState(null);

  const randomId = () => Math.random().toString(36).slice(2, 7);
  const redirectToStakeholders = () =>
    replaceRoute('projects/stakeholders', { projectId });

  const onSubmit = (values) => {
    const underscoreFilter = () =>
      columns
        .filter((column) => column.name)
        .map((column) => ({
          ...column,
          name: column.name.replace(/_/g, '-'),
        }));

    const composedData = (editorData) => ({
      ...values,
      stakeholderFields: underscoreFilter(),
      changes_comment: editorData.blocks.length
        ? JSON.stringify(editorData)
        : null,
    });

    getReadyStatusPromise(commentEditorInstance).then(
      getEditorSaveDataPromise(commentEditorInstance).then((editorData) =>
        updateProject(projectId, composedData(editorData))
          .then(() => redirectToStakeholders())
          .catch(mapErrors)
      )
    );
  };

  const validate = (values) => stakeholdersSettingsValidate(values, columns);

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result.map((item, index) => ({ ...item, priority: index }));
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) return;

    const items = reorder(
      columns,
      result.source.index,
      result.destination.index
    );

    setColumns(items);
  };

  const onCreateColumn = () =>
    setColumns([
      ...columns,
      {
        id: null,
        editor: false,
        priority: columns.length,
        name: '',
        allowDelete: true,
        allowEditor: true,
        draggableId: randomId(),
        tableShow: true,
      },
    ]);

  const onDeleteColumn = (draggableId) => {
    const { name } = columns.find(
      (column) => column.draggableId === draggableId
    );
    if (
      !window.confirm(
        'Are you sure you want to delete this column? All data in the column will be deleted as well.'
      )
    )
      return;

    setColumns(columns.filter((column) => column.draggableId !== draggableId));
  };

  const onColumnNameChange = (value, itemIndex) => {
    setColumns(
      columns.map((item, index) =>
        itemIndex === index ? { ...item, name: value } : item
      )
    );
  };

  const onContentTypeChange = (itemIndex) => {
    if (
      columns[itemIndex].editor &&
      columns[itemIndex].id &&
      project.stakeholderFields[itemIndex].editor &&
      !window.confirm(
        'Are you sure you want to change the field type? Text formatting will be lost.'
      )
    )
      return;

    setColumns(
      columns.map((item, index) =>
        itemIndex === index ? { ...item, editor: !item.editor } : item
      )
    );
  };

  const onTableShowChange = (itemIndex) => {
    setColumns(
      columns.map((item, index) =>
        itemIndex === index ? { ...item, tableShow: !item.tableShow } : item
      )
    );
  };

  useEffect(() => {
    retrieveProject(projectId, false);
  }, []);

  useEffect(() => {
    project &&
      setColumns(
        project.stakeholderFields.map((item) => ({
          ...item,
          draggableId: item.draggableId || randomId(),
        }))
      );

    project &&
      setInitial({
        topLeftQuadrantLabel: project.topLeftQuadrantLabel,
        topRightQuadrantLabel: project.topRightQuadrantLabel,
        bottomLeftQuadrantLabel: project.bottomLeftQuadrantLabel,
        bottomRightQuadrantLabel: project.bottomRightQuadrantLabel,
      });
  }, [project]);

  return (
    <React.Fragment>
      {project && <StakeholdersSettingsHeader project={project} />}
      <SubHeader flexJustify small>
        <h2>Customize Stakeholder Analysis</h2>
      </SubHeader>

      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              <Row>
                <Col sm={10} smOffset={1}>
                  {columns ? (
                    <Form
                      onSubmit={onSubmit}
                      validate={validate}
                      onDragEnd={onDragEnd}
                      onCreateColumn={onCreateColumn}
                      onColumnNameChange={onColumnNameChange}
                      onDeleteColumn={onDeleteColumn}
                      onContentTypeChange={onContentTypeChange}
                      onTableShowChange={onTableShowChange}
                      setCommentEditorInstance={setCommentEditorInstance}
                      initialValues={initial}
                      data={columns}
                      projectId={projectId}
                      component={StakeholdersSettingsFrom}
                    />
                  ) : null}
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Grid>
    </React.Fragment>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StakeholderSettingsFormContainer);
